import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { BunnyCharacter } from '../components/BunnyCharacter'
import { api } from '../api/client'
import type { Hunt, Egg } from '../types'

export function EditHunt() {
  const { huntId } = useParams<{ huntId: string }>()
  const navigate = useNavigate()
  const [hunt, setHunt] = useState<Hunt | null>(null)
  const [eggs, setEggs] = useState<Egg[]>([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [confirmReset, setConfirmReset] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const [nameValue, setNameValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadHunt()
  }, [huntId])

  const loadHunt = async () => {
    if (!huntId) return
    try {
      const result = await api.hunts.get(huntId)
      setHunt(result.hunt)
      setEggs(result.eggs)
      setNameValue(result.hunt.name)
    } catch {
      setError('Kunde inte ladda jakten')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveName = async () => {
    if (!huntId || !nameValue.trim()) return
    await api.hunts.update(huntId, { name: nameValue.trim() })
    setHunt((h) => h ? { ...h, name: nameValue.trim() } : h)
    setEditingName(false)
  }

  const handleGenerate = async () => {
    if (!huntId || !hunt) return
    setGenerating(true)
    setError(null)
    try {
      await api.generate(
        huntId,
        eggs.map((e) => ({ description: e.description, difficulty: e.difficulty })),
        { children_ages: [7, 9] }
      )
      await loadHunt()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Genereringen misslyckades')
    } finally {
      setGenerating(false)
    }
  }

  const handleActivate = async () => {
    if (!huntId) return
    await api.hunts.update(huntId, { status: 'active' })
    setHunt((h) => h ? { ...h, status: 'active' } : h)
  }

  const handleDeactivate = async () => {
    if (!huntId) return
    await api.hunts.update(huntId, { status: 'draft' })
    setHunt((h) => h ? { ...h, status: 'draft' } : h)
  }

  const handleSkipEgg = async (egg: Egg) => {
    if (!huntId) return
    if (confirm(`Hoppa over agg ${egg.order_index}? Barnen far nasta ledtrad direkt.`)) {
      await api.hunts.skipEgg(huntId, egg.id)
      await loadHunt()
    }
  }

  const handleReset = async () => {
    if (!huntId) return
    setResetting(true)
    setError(null)
    try {
      await api.hunts.reset(huntId)
      setHunt((h) => h ? { ...h, status: 'active' } : h)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kunde inte återställa jakten')
    } finally {
      setResetting(false)
      setConfirmReset(false)
    }
  }

  const handleDelete = async () => {
    if (!huntId) return
    if (confirm(`Ta bort "${hunt?.name}"? Alla ägg och framsteg raderas. Kan inte ångras.`)) {
      await api.hunts.delete(huntId)
      navigate('/parent')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-dvh bg-cream">
        <BunnyCharacter size="md" animate="think" />
      </div>
    )
  }

  if (!hunt) {
    return (
      <div className="flex flex-col items-center justify-center min-h-dvh bg-cream p-6">
        <p className="font-body text-lg text-ink/60">Jakten hittades inte</p>
        <Button className="mt-4" onClick={() => navigate('/parent')}>Tillbaka</Button>
      </div>
    )
  }

  const statusColors: Record<string, string> = {
    draft: 'bg-sunshine/20 text-sunshine',
    active: 'bg-mint/20 text-mint',
    completed: 'bg-ink/10 text-ink/40',
  }
  const statusLabels: Record<string, string> = {
    draft: 'Utkast',
    active: 'Aktiv',
    completed: 'Klar',
  }

  const hasAiContent = eggs.some((e) => e.clue && e.clue.length > 0)

  return (
    <div className="min-h-dvh p-6 bg-cream">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/parent')}>
            ←
          </Button>
          <div className="flex-1">
            {editingName ? (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-xl border-2 border-turquoise/30 font-heading text-lg
                             focus:border-turquoise focus:outline-none bg-white"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                />
                <Button size="sm" onClick={handleSaveName}>Spara</Button>
              </div>
            ) : (
              <h1
                className="font-heading text-2xl text-ink cursor-pointer hover:text-turquoise transition-colors"
                onClick={() => setEditingName(true)}
                title="Klicka för att byta namn"
              >
                {hunt.name}
              </h1>
            )}
          </div>
          <span className={`px-3 py-1 rounded-full font-heading text-sm ${statusColors[hunt.status]}`}>
            {statusLabels[hunt.status]}
          </span>
        </div>

        {error && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-coral font-body mb-4"
          >
            {error}
          </motion.p>
        )}

        {/* Story preview */}
        {hunt.story_intro && (
          <Card className="mb-4 border-l-4 border-lavender">
            <p className="font-heading text-sm text-lavender mb-1">Berättelse (intro)</p>
            <p className="font-body text-base text-ink/70">{hunt.story_intro}</p>
          </Card>
        )}

        {/* Eggs list */}
        <h2 className="font-heading text-lg text-ink mb-3">Ägg ({eggs.length} st)</h2>
        <div className="flex flex-col gap-3 mb-6">
          <AnimatePresence>
            {eggs.map((egg, i) => (
              <motion.div
                key={egg.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Card>
                  <div className="flex gap-3 items-start">
                    <div className="w-10 h-10 rounded-full bg-turquoise/20 flex items-center justify-center font-heading text-lg text-turquoise flex-shrink-0">
                      {egg.order_index}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-heading text-base text-ink">{egg.description}</p>
                      <div className="flex gap-2 mt-1">
                        <span className="font-body text-sm text-ink/40">
                          Svårighet: {'●'.repeat(egg.difficulty)}{'○'.repeat(3 - egg.difficulty)}
                        </span>
                      </div>
                      {egg.clue && (
                        <div className="mt-2 bg-lavender/10 rounded-lg px-3 py-2">
                          <p className="font-body text-sm text-ink/60 italic">"{egg.clue}"</p>
                        </div>
                      )}
                      {egg.hint_1 && (
                        <div className="mt-1 flex gap-1 flex-wrap">
                          <span className="text-xs bg-sunshine/20 rounded px-2 py-0.5 font-body">Hint 1</span>
                          <span className="text-xs bg-sunshine/30 rounded px-2 py-0.5 font-body">Hint 2</span>
                          <span className="text-xs bg-sunshine/40 rounded px-2 py-0.5 font-body">Hint 3</span>
                        </div>
                      )}
                      {hunt.status === 'active' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mt-2 text-coral/60 hover:text-coral"
                          onClick={() => handleSkipEgg(egg)}
                        >
                          Hoppa over
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          {/* Generate AI content */}
          {hunt.status === 'draft' && (
            <Button
              size="lg"
              variant="secondary"
              className="w-full"
              onClick={handleGenerate}
              loading={generating}
              disabled={generating || eggs.length === 0}
            >
              {hasAiContent ? 'Generera om berättelse' : 'Generera berättelse med AI'}
            </Button>
          )}

          {generating && (
            <div className="flex flex-col items-center gap-2 py-4">
              <BunnyCharacter size="sm" animate="think" />
              <p className="font-body text-sm text-ink/50">Påskharen skriver berättelsen...</p>
            </div>
          )}

          {/* Activate / Deactivate */}
          {hunt.status === 'draft' && hasAiContent && (
            <Button size="lg" className="w-full" onClick={handleActivate}>
              Aktivera jakt
            </Button>
          )}

          {hunt.status === 'active' && (
            <Button size="lg" variant="ghost" className="w-full" onClick={handleDeactivate}>
              Pausa jakt (tillbaka till utkast)
            </Button>
          )}

          {/* Reset — shown for completed or active hunts */}
          {(hunt.status === 'completed' || hunt.status === 'active') && (
            <AnimatePresence mode="wait">
              {confirmReset ? (
                <motion.div
                  key="confirm"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="bg-coral/10 rounded-2xl p-4 flex flex-col gap-3"
                >
                  <p className="font-heading text-base text-ink text-center">
                    Återställ jakten?
                  </p>
                  <p className="font-body text-sm text-ink/60 text-center">
                    Alla spelares framsteg raderas. Äggen och ledtrådarna behålls. Jakten kan spelas igen.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-1"
                      onClick={() => setConfirmReset(false)}
                      disabled={resetting}
                    >
                      Avbryt
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 bg-coral text-white"
                      onClick={handleReset}
                      loading={resetting}
                      disabled={resetting}
                    >
                      Ja, återställ!
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <motion.div key="btn" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-coral/70 hover:text-coral"
                    onClick={() => setConfirmReset(true)}
                  >
                    🔄 Starta om jakten från början
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          )}

          {/* Delete */}
          <Button
            variant="ghost"
            size="sm"
            className="text-coral/60 hover:text-coral mt-4"
            onClick={handleDelete}
          >
            Ta bort jakt
          </Button>
        </div>

        {/* Story finale preview */}
        {hunt.story_finale && (
          <Card className="mt-6 border-l-4 border-mint">
            <p className="font-heading text-sm text-mint mb-1">Berättelse (final)</p>
            <p className="font-body text-base text-ink/70">{hunt.story_finale}</p>
          </Card>
        )}
      </div>
    </div>
  )
}
